import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { calendarApi } from '../lib/api';
import { Plus, Trash2, CalendarDays, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function EventItem({ event, onDelete }) {
  const isPersonal = event.type === 'personal';
  return (
    <div className="flex items-center gap-2 py-1.5 group" data-testid={`event-${event.id}`}>
      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
      <span className="flex-1 text-sm text-[#F0EDE8] truncate">{event.title}</span>
      {isPersonal && (
        <Button size="icon" variant="ghost"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#E55B5B] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg"
          onClick={() => onDelete(event.id)} data-testid={`delete-event-${event.id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function AddEventForm({ selectedDate, onAdd, onCancel }) {
  const [title, setTitle] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onAdd(title.trim());
    setTitle('');
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 mt-2">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
        placeholder={`Event for ${new Date(selectedDate + 'T12:00:00').toLocaleDateString()}`}
        className="flex-1 h-8 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
        data-testid="new-event-input" />
      <Button type="submit" size="sm" className="h-8 bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl"
        data-testid="add-event-submit">
        <Plus className="w-3 h-3" />
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-8 text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl" onClick={onCancel}>
        <X className="w-3 h-3" />
      </Button>
    </form>
  );
}

export function CalendarPanel({ onClose, refreshKey = 0 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await calendarApi.getEvents();
      setEvents(res.data);
    } catch (err) { void err; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents, refreshKey]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  // Dates that have events (for calendar highlighting)
  const eventDates = useMemo(() => {
    return Object.keys(eventsByDate).map(d => new Date(d + 'T12:00:00'));
  }, [eventsByDate]);

  // Events for selected date
  const selectedDateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : null;
  const selectedEvents = selectedDateStr ? (eventsByDate[selectedDateStr] || []) : [];

  const handleAddEvent = useCallback(async (title) => {
    if (!selectedDateStr) return;
    try {
      const res = await calendarApi.createEvent({ title, date: selectedDateStr });
      setEvents(prev => [...prev, res.data]);
      setShowAddForm(false);
      toast.success('Event added');
    } catch (err) {
      toast.error('Failed to add event');
      void err;
    }
  }, [selectedDateStr]);

  const handleDeleteEvent = useCallback(async (eventId) => {
    try {
      await calendarApi.deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event removed');
    } catch (err) {
      toast.error('Failed to remove event');
      void err;
    }
  }, []);

  // Custom day render to show dots for events
  const modifiers = useMemo(() => ({
    hasEvent: eventDates
  }), [eventDates]);

  const modifiersClassNames = {
    hasEvent: 'has-event-marker',
  };

  if (loading) {
    return (
      <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-6 flex items-center justify-center h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-[#4A9D6E]" />
      </div>
    );
  }

  return (
    <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 md:p-6" data-testid="calendar-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#4A9D6E]" />
          <h3 className="font-bold text-[#F0EDE8] tracking-tight text-lg">Calendar</h3>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl md:hidden"
          onClick={onClose} data-testid="close-calendar">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Calendar */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-shrink-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => { setSelectedDate(date); setShowAddForm(false); }}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="rounded-xl border border-[#33383F] bg-[#1A1D21] p-3"
          />
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-3 px-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#D4855A]" />
              <span className="text-xs text-[#9CA3AF]">Created</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#E5A84B]" />
              <span className="text-xs text-[#9CA3AF]">Due</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#4A9D6E]" />
              <span className="text-xs text-[#9CA3AF]">Personal</span>
            </div>
          </div>
        </div>

        {/* Events for selected date */}
        <div className="flex-1 min-w-0">
          {selectedDate ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-[#F0EDE8]">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </h4>
                <Badge variant="secondary" className="bg-[#2C3038] text-[#9CA3AF] rounded-xl">
                  {selectedEvents.length}
                </Badge>
              </div>
              <ScrollArea className="max-h-[200px]">
                {selectedEvents.length > 0 ? (
                  <div className="space-y-1">
                    {selectedEvents.map(event => (
                      <EventItem key={event.id} event={event} onDelete={handleDeleteEvent} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#555D68] py-2">No events on this date</p>
                )}
              </ScrollArea>
              {showAddForm ? (
                <AddEventForm selectedDate={selectedDateStr} onAdd={handleAddEvent} onCancel={() => setShowAddForm(false)} />
              ) : (
                <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}
                  className="mt-3 border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl w-full"
                  data-testid="add-event-button">
                  <Plus className="w-3 h-3 mr-1.5" />Add Event
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-[#555D68] text-sm py-8">
              Select a date to view or add events
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
